import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { PromiseStatuses } from "../../lib/utils"
import { NewSotService } from "./service"
import { headQuarterResponseDTO, SOTCreationUpdateDTO, SOTErrorsDTO, sotFilterDTO } from "./dto"

interface SotState {
    onEdit: boolean

    // REQUEST
    sotCreationRequest: SOTCreationUpdateDTO
    sotUpdateRequest: SOTCreationUpdateDTO
    sotIdToUpdate: string

    // ERRORS
    sotErrors: SOTErrorsDTO

    // FILTERS
    sotFilters: sotFilterDTO

    // STATUS
    sotCreationStatus: PromiseStatuses
    sotUpdateStatus: PromiseStatuses
    sotRemoveStatus: PromiseStatuses
    sotRestoreStatus: PromiseStatuses
    sotDeleteStatus: PromiseStatuses
    sotValidationStatus: PromiseStatuses
    responseHeadQuarterStatus: PromiseStatuses
    responseHeadQuarterByIdStatus: PromiseStatuses

    // RESPONSE
    responseHeadQuarter?: headQuarterResponseDTO
    responseHeadQuarterById?: headQuarterResponseDTO
}

const initialState: SotState = {
    onEdit: false,

    sotCreationRequest: {},
    sotUpdateRequest: {},
    sotIdToUpdate: '',

    sotErrors: {
        code: false,
        region: false,
        address: false,
        city: false,
        province: false,
        email: false
    },

    sotFilters: {
        page: 0,
        itemsPerPage: 0,
        order: true
    },

    sotCreationStatus: 'idle',
    sotUpdateStatus: 'idle',
    sotRemoveStatus: 'idle',
    sotRestoreStatus: 'idle',
    sotDeleteStatus: 'idle',
    sotValidationStatus: 'idle',
    responseHeadQuarterStatus: 'idle',
    responseHeadQuarterByIdStatus: 'idle'
}

export const SotValidation = createAsyncThunk(
    'sot/Validation',
    async (request: SOTCreationUpdateDTO, thunkApi): Promise<void> => {
        let isValid = true
        thunkApi.dispatch(resetSOTErrors())

        if(request.code === undefined || request.code === '') {
            thunkApi.dispatch(setValidateCode(true))
            isValid = false
        }

        if(request.region === undefined || request.region === '') {
            thunkApi.dispatch(setValidateRegion(true))
            isValid = false
        }

        if(request.address === undefined || request.address === '') {
            thunkApi.dispatch(setValidateAddress(true))
            isValid = false
        }

        if(request.city === undefined || request.city === '') {
            thunkApi.dispatch(setValidateCity(true))
            isValid = false
        }

        if(request.province === undefined || request.province === '') {
            thunkApi.dispatch(setValidateProvince(true))
            isValid = false
        }

        if(request.email === undefined || request.email === '') {
            thunkApi.dispatch(setValidateEmail(true))
            isValid = false
        }

        console.log(request);
        

        if (!isValid) {
            return Promise.reject()
        }
        return Promise.resolve()
        
    }
)

export const SOTCreation = createAsyncThunk(
    'sot/Creation',
    async (body: SOTCreationUpdateDTO, thunkApi): Promise<any> => {
        const SotService = NewSotService()
        return SotService.CreateSOT(body).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    }
)

export const SOTUpdate = createAsyncThunk(
    'sot/Update',
    async (request: { body: SOTCreationUpdateDTO, id: string }, thunkApi): Promise<any> => {
        const SotService = NewSotService()
        return SotService.UpdateSOT(request.body, request.id).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    }
)

export const SOTRemove = createAsyncThunk(
    'sot/Remove',
    async (id: string, thunkApi): Promise<any> => {
        const SotService = NewSotService()
        return SotService.RemoveSOT(id).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    }
)

export const SOTRestore = createAsyncThunk(
    'sot/Restore',
    async (id: string, thunkApi): Promise<any> => {
        const SotService = NewSotService()
        return SotService.RestoreSOT(id).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    }
)

export const SOTDelete = createAsyncThunk(
    'sot/Delete',
    async (id: string, thunkApi): Promise<any> => {
        const SotService = NewSotService()
        return SotService.DeleteSOT(id).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    }
)

export const GetHeadQuarter = createAsyncThunk(
    'sot/GetHeadQuarter',
    async (filters: sotFilterDTO, thunkApi): Promise<any> => {
        const SotService = NewSotService()
        return SotService.GetHeadQuarter(filters)
    }
)

export const FindSotById = createAsyncThunk(
    'sot/FindSotById',
    async (id: string, thunkApi): Promise<any> => {
        const SotService = NewSotService()

        return SotService.FindSotById(id)
    }
)


const SotSlice = createSlice({
    name: 'sot/slice',
    initialState,
    reducers: {
        setOnEdit: (state, action) => {
            state.onEdit = action.payload
        },

        // REQUEST
        setSotCreationManager: (state, action) => {
            state.sotCreationRequest.manager = action.payload
        },
        setSotCreationPhone: (state, action) => {
            state.sotCreationRequest.phone = action.payload
        },
        setSotCreationFax: (state, action) => {
            state.sotCreationRequest.fax = action.payload
        },
        setSotCreationEmail: (state, action) => {
            state.sotCreationRequest.email = action.payload
        },
        setSotCreationPec: (state, action) => {
            state.sotCreationRequest.pec = action.payload
        },
        setSotCreationName: (state, action) => {
            state.sotCreationRequest.name = action.payload
        },
        setSotCreationCode: (state, action) => {
            state.sotCreationRequest.code = action.payload
        },
        setSotCreationRegion: (state, action) => {
            state.sotCreationRequest.region = action.payload
        },
        setSotCreationAddress: (state, action) => {
            state.sotCreationRequest.address = action.payload
        },
        setSotCreationZipcode: (state, action) => {
            state.sotCreationRequest.zipCode = action.payload
        },
        setSotCreationCity: (state, action) => {
            state.sotCreationRequest.city = action.payload
        },
        setSotCreationProvince: (state, action) => {
            state.sotCreationRequest.province = action.payload
        },
        resetSotCreationRequest: (state) => {
            state.sotCreationRequest = {
                manager: '',
                pec: '',
                phone: '',
                province: '',
                city: '',
                code: '',
                region: '',
                zipCode: '',
                address: '',
                fax: '',
                name: '',
                email: ''
            }
        },

        setSotIdToUpdate: (state, action) => {
            state.sotIdToUpdate = action.payload
        },
        setSotUpdateManager: (state, action) => {
            state.sotUpdateRequest.manager = action.payload
        },
        setSotUpdatePhone: (state, action) => {
            state.sotUpdateRequest.phone = action.payload
        },
        setSotUpdateFax: (state, action) => {
            state.sotUpdateRequest.fax = action.payload
        },
        setSotUpdateEmail: (state, action) => {
            state.sotUpdateRequest.email = action.payload
        },
        setSotUpdatePec: (state, action) => {
            state.sotUpdateRequest.pec = action.payload
        },
        setSotUpdateName: (state, action) => {
            state.sotUpdateRequest.name = action.payload
        },
        setSotUpdateCode: (state, action) => {
            state.sotUpdateRequest.code = action.payload
        },
        setSotUpdateRegion: (state, action) => {
            state.sotUpdateRequest.region = action.payload
        },
        setSotUpdateAddress: (state, action) => {
            state.sotUpdateRequest.address = action.payload
        },
        setSotUpdateZipcode: (state, action) => {
            state.sotUpdateRequest.zipCode = action.payload
        },
        setSotUpdateCity: (state, action) => {
            state.sotUpdateRequest.city = action.payload
        },
        setSotUpdateProvince: (state, action) => {
            state.sotUpdateRequest.province = action.payload
        },
        resetSotCUpdateRequest: (state) => {
            state.sotUpdateRequest = {}
        },

        // ERRORS
        setValidateCode: (state, action) => {
            state.sotErrors.code = action.payload
        },
        setValidateRegion: (state, action) => {
            state.sotErrors.region = action.payload
        },
        setValidateAddress: (state, action) => {
            state.sotErrors.address = action.payload
        },
        setValidateCity: (state, action) => {
            state.sotErrors.city = action.payload
        },
        setValidateProvince: (state, action) => {
            state.sotErrors.province = action.payload
        },
        setValidateEmail: (state, action) => {
            state.sotErrors.email = action.payload
        },
        resetSOTErrors: (state) => {
            state.sotErrors = {
                code: false,
                region: false,
                address: false,
                city: false,
                province: false,
                email: false
            }
        },


        // FILTERS
        setGetAllSotItemsPerPage: (state, action) => {
            state.sotFilters.itemsPerPage = action.payload
        },
        setGetAllSotPage: (state, action) => {
            state.sotFilters.page = action.payload
        },
        resetSotFilters: (state, action) => {
            state.sotFilters = {
                itemsPerPage: 0,
                page: 0,
                order: true
            }
        },

        // STATUS
        resetSotValidationStatus: (state) => {
            state.sotValidationStatus = 'idle'
        },
        resetSotCreationStatus: (state) => {
            state.sotCreationStatus = 'idle'
        },
        resetSotUpdateStatus: (state) => {
            state.sotUpdateStatus = 'idle'
        },
        resetSotRemoveStatus: (state) => {
            state.sotRemoveStatus = 'idle'
        },
        resetSotRestoreStatus: (state) => {
            state.sotRestoreStatus = 'idle'
        },
        resetSotDeleteStatus: (state) => {
            state.sotDeleteStatus = 'idle'
        },
        resetGetAllSot: (state) => {
            state.responseHeadQuarterStatus = 'idle'
        },
        resetGetSotById: (state) => {
            state.responseHeadQuarterByIdStatus = 'idle'
        }

    },
    extraReducers(builder) {
        builder

            // CREATION
            .addCase(SOTCreation.pending, (state) => {
                state.sotCreationStatus = 'loading'
            })
            .addCase(SOTCreation.fulfilled, (state) => {
                state.sotCreationStatus = 'successfully'
            })
            .addCase(SOTCreation.rejected, (state, action) => {
                state.sotCreationStatus = 'failed'
            })

            // UPDATE
            .addCase(SOTUpdate.pending, (state) => {
                state.sotUpdateStatus = 'loading'
            })
            .addCase(SOTUpdate.fulfilled, (state) => {
                state.sotUpdateStatus = 'successfully'
            })
            .addCase(SOTUpdate.rejected, (state, action) => {
                state.sotUpdateStatus = 'failed'
            })

            // REMOVE
            .addCase(SOTRemove.pending, (state) => {
                state.sotRemoveStatus = 'loading'
            })
            .addCase(SOTRemove.fulfilled, (state) => {
                state.sotRemoveStatus = 'successfully'
            })
            .addCase(SOTRemove.rejected, (state, action) => {
                state.sotRemoveStatus = 'failed'
            })

            // RESTORE
            .addCase(SOTRestore.pending, (state) => {
                state.sotRestoreStatus = 'loading'
            })
            .addCase(SOTRestore.fulfilled, (state) => {
                state.sotRestoreStatus = 'successfully'
            })
            .addCase(SOTRestore.rejected, (state, action) => {
                state.sotRestoreStatus = 'failed'
            })

            // DELETE
            .addCase(SOTDelete.pending, (state) => {
                state.sotDeleteStatus = 'loading'
            })
            .addCase(SOTDelete.fulfilled, (state) => {
                state.sotDeleteStatus = 'successfully'
            })
            .addCase(SOTDelete.rejected, (state, action) => {
                state.sotDeleteStatus = 'failed'
            })

            // GET ALL 
            .addCase(GetHeadQuarter.pending, (state) => {
                state.responseHeadQuarterStatus = 'loading'
            })
            .addCase(GetHeadQuarter.fulfilled, (state, action) => {
                state.responseHeadQuarterStatus = 'successfully'
                state.responseHeadQuarter = action.payload
            })
            .addCase(GetHeadQuarter.rejected, (state, action) => {
                state.responseHeadQuarterStatus = 'failed'
            })

            // GET BY ID
            .addCase(FindSotById.pending, (state) => {
                state.responseHeadQuarterByIdStatus = 'loading'
            })
            .addCase(FindSotById.fulfilled, (state, action) => {
                state.responseHeadQuarterByIdStatus = 'successfully'
                state.responseHeadQuarterById = action.payload
            })
            .addCase(FindSotById.rejected, (state, action) => {
                state.responseHeadQuarterByIdStatus = 'failed'
            })

            // VALIDATION
            .addCase(SotValidation.pending, (state) => {
                state.sotValidationStatus = 'loading'
            })
            .addCase(SotValidation.fulfilled, (state) => {
                state.sotValidationStatus = 'successfully'
            })
            .addCase(SotValidation.rejected, (state, action) => {
                state.sotValidationStatus = 'failed'
            })

    },
})

export const {
    setOnEdit,
    setSotCreationAddress,
    setSotCreationCity,
    setSotCreationCode,
    setSotCreationEmail,
    setSotCreationFax,
    setSotCreationManager,
    setSotCreationName,
    setSotCreationPec,
    setSotCreationPhone,
    setSotCreationProvince,
    setSotCreationRegion,
    setSotCreationZipcode,
    setSotIdToUpdate,
    setSotUpdateAddress,
    setSotUpdateCity,
    setSotUpdateCode,
    setSotUpdateEmail,
    setSotUpdateFax,
    setSotUpdateManager,
    setSotUpdateName,
    setSotUpdatePec,
    setSotUpdatePhone,
    setSotUpdateProvince,
    setSotUpdateRegion,
    setSotUpdateZipcode,
    resetSotCUpdateRequest,
    resetSotCreationRequest,
    setGetAllSotItemsPerPage,
    setGetAllSotPage,
    setValidateAddress,
    setValidateCity,
    setValidateCode,
    setValidateEmail,
    setValidateProvince,
    setValidateRegion,
    resetSOTErrors,
    resetSotFilters,
    resetSotCreationStatus,
    resetSotDeleteStatus,
    resetSotRemoveStatus,
    resetSotRestoreStatus,
    resetSotUpdateStatus,
    resetGetAllSot,
    resetGetSotById,
    resetSotValidationStatus
} = SotSlice.actions

export default SotSlice.reducer